import React, { useMemo, useState } from 'react';
import type { GenerationResult, PageFacts, ValidationIssue, ValidationReport } from '../types';
import { toJsonLdString, toMicrodata, toRdfa } from '../lib/format-converters';
import { buildGraph, combineValidation } from '../lib/graph';
import { hasExistingOfType } from '../lib/existing';
import { schemaTypeIcon } from '../lib/icons';
import { ExportMenu } from './ExportMenu';

interface GeneratedSchemaProps {
  generations: GenerationResult[];
  facts: PageFacts;
  busy: boolean;
  /** When non-null, only the suggestion matching this type spins */
  generatingType: string | null;
  onChooseDifferentType: () => void;
  onAddCompanion: (typeName: string, augment: boolean) => void;
  onRemove: (index: number) => void;
}

type Format = 'jsonld' | 'microdata' | 'rdfa';

const FORMAT_LABELS: Record<Format, string> = {
  jsonld: 'JSON-LD',
  microdata: 'Microdata',
  rdfa: 'RDFa',
};

const ALL_TYPES = [
  'Article', 'NewsArticle', 'BlogPosting', 'CreativeWork', 'Book', 'Movie',
  'TVSeries', 'MusicRecording', 'Recipe', 'AudioObject', 'ImageObject',
  'VideoObject', 'Event', 'Organization', 'Person', 'Place', 'LocalBusiness',
  'Restaurant', 'Product', 'Offer', 'AggregateOffer', 'Review', 'AggregateRating',
  'FAQPage', 'HowTo', 'JobPosting', 'Course', 'BreadcrumbList',
];

// Suggested companions per primary type — types Google rewards in combination.
const COMPANION_SUGGESTIONS: Record<string, string[]> = {
  Article: ['BreadcrumbList', 'Organization', 'Person'],
  NewsArticle: ['BreadcrumbList', 'Organization', 'Person'],
  BlogPosting: ['BreadcrumbList', 'Organization', 'Person'],
  Recipe: ['BreadcrumbList', 'Person', 'AggregateRating', 'VideoObject'],
  Product: ['BreadcrumbList', 'Organization', 'AggregateRating', 'Review'],
  LocalBusiness: ['BreadcrumbList', 'AggregateRating', 'Review'],
  Restaurant: ['BreadcrumbList', 'AggregateRating', 'Review'],
  Event: ['BreadcrumbList', 'Organization', 'Place'],
  JobPosting: ['BreadcrumbList', 'Organization'],
  Course: ['BreadcrumbList', 'Organization'],
  Book: ['BreadcrumbList', 'Person'],
  Movie: ['BreadcrumbList', 'AggregateRating', 'Person'],
  TVSeries: ['BreadcrumbList', 'AggregateRating', 'Person'],
  HowTo: ['BreadcrumbList', 'VideoObject'],
  FAQPage: ['BreadcrumbList'],
  Organization: ['BreadcrumbList'],
  Person: ['Organization'],
  default: ['BreadcrumbList', 'Organization'],
};

export const GeneratedSchema: React.FC<GeneratedSchemaProps> = ({
  generations,
  facts,
  busy,
  generatingType,
  onChooseDifferentType,
  onAddCompanion,
  onRemove,
}) => {
  const [format, setFormat] = useState<Format>('jsonld');
  const [activeEntity, setActiveEntity] = useState(0);
  const [copyToast, setCopyToast] = useState(false);

  // Clamp active entity if the array shrinks
  const clampedActive = Math.min(activeEntity, Math.max(0, generations.length - 1));

  const graph = useMemo(() => buildGraph(generations), [generations]);
  const code = useMemo(() => {
    switch (format) {
      case 'jsonld':
        return toJsonLdString(graph, { script: true });
      case 'microdata':
        return toMicrodata(graph);
      case 'rdfa':
        return toRdfa(graph);
    }
  }, [format, graph]);

  const sourceUrl = generations[0]?.sourceUrl;
  const validatorUrl = sourceUrl
    ? `https://search.google.com/test/rich-results?url=${encodeURIComponent(sourceUrl)}`
    : null;

  const combinedValidation = useMemo(() => combineValidation(generations), [generations]);
  const isGraph = generations.length > 1;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopyToast(true);
      setTimeout(() => setCopyToast(false), 1800);
    } catch {
      // ignore
    }
  };

  const activeResult = generations[clampedActive];
  const filledCount = activeResult?.fieldNotes.filter((n) => n.status === 'filled').length ?? 0;
  const placeholderCount = activeResult?.fieldNotes.filter((n) => n.status === 'placeholder').length ?? 0;
  const preservedCount = activeResult?.fieldNotes.filter((n) => n.status === 'preserved').length ?? 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <span className="eyebrow">{isGraph ? `@graph · ${generations.length} entities` : (activeResult?.augmented ? 'Augmented' : 'Generated')}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span className="type-icon-circle" aria-hidden>
              {schemaTypeIcon(activeResult?.type || generations[0]?.type || '', { size: 16 })}
            </span>
            <h2 className="h2" style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: 24 }}>
              {generations.map((g) => g.type).join(' + ')}
            </h2>
          </div>
          {activeResult && (
            <p className="meta" style={{ margin: 0 }}>
              {isGraph && <>Active: <code>{activeResult.type}</code> · </>}
              {activeResult.augmented && preservedCount > 0 && (
                <>{preservedCount} preserved · </>
              )}
              {filledCount} filled
              {placeholderCount > 0 && ` · ${placeholderCount} placeholder${placeholderCount === 1 ? '' : 's'}`}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onChooseDifferentType}>
            Pick a different type
          </button>
          <ExportMenu
            generations={generations}
            graph={graph}
            format={format}
            code={code}
            sourceUrl={sourceUrl || ''}
          />
        </div>
      </div>

      {activeResult?.augmented && (
        <div
          style={{
            marginBottom: 20,
            padding: '12px 16px',
            background: 'var(--signal-wash)',
            border: '1px solid rgba(43,108,255,0.2)',
            borderRadius: 4,
            fontSize: 13.5,
            color: 'var(--ink-2)',
          }}
        >
          <strong style={{ fontWeight: 600 }}>Strict superset.</strong>{' '}
          Started from your existing on-page JSON-LD for <code>{activeResult.type}</code> and only added missing fields.
          Every property already there is preserved verbatim.
          {activeResult.existingBase && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--signal)', fontWeight: 500 }}>
                See the existing JSON-LD we started from
              </summary>
              <pre
                style={{
                  marginTop: 10,
                  padding: 14,
                  background: 'var(--paper-2)',
                  border: '1px solid var(--rule)',
                  borderRadius: 4,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: 'var(--ink)',
                  overflow: 'auto',
                  whiteSpace: 'pre',
                }}
              >
                <code>{JSON.stringify(activeResult.existingBase, null, 2)}</code>
              </pre>
            </details>
          )}
        </div>
      )}

      <ValidationPanel report={combinedValidation} />

      {activeResult && activeResult.warnings.length > 0 && (
        <div
          style={{
            marginBottom: 20,
            padding: '14px 18px',
            background: 'var(--score-mid-wash)',
            border: '1px solid rgba(181,132,42,0.3)',
            borderRadius: 4,
          }}
        >
          <span className="eyebrow" style={{ display: 'block', marginBottom: 8, color: 'var(--score-mid)' }}>
            Before you publish ({activeResult.type})
          </span>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.6 }}>
            {activeResult.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="tabs" role="tablist" aria-label="Output format">
        {(Object.keys(FORMAT_LABELS) as Format[]).map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={format === f}
            className={`tab ${format === f ? 'is-active' : ''}`}
            onClick={() => setFormat(f)}
          >
            {FORMAT_LABELS[f]}
          </button>
        ))}
      </div>

      <div style={{ position: 'relative' }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={handleCopy}
          style={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}
        >
          {copyToast ? 'Copied' : 'Copy'}
        </button>
        <pre
          style={{
            margin: 0,
            padding: '20px 22px',
            background: 'var(--graphite)',
            color: 'var(--paper)',
            borderRadius: 4,
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            lineHeight: 1.55,
            overflowX: 'auto',
            whiteSpace: 'pre',
          }}
        >
          <code>{code}</code>
        </pre>
      </div>

      <p className="meta" style={{ marginTop: 12 }}>
        {format === 'jsonld' && 'Paste this <script> block into the <head> of your page.'}
        {format === 'microdata' && 'Wrap your existing markup with these attributes, or paste this fragment into the <body>.'}
        {format === 'rdfa' && 'RDFa attribute markup. Add to existing elements or paste as-is in the <body>.'}
      </p>

      {validatorUrl && (
        <p className="meta" style={{ marginTop: 18 }}>
          <a href={validatorUrl} target="_blank" rel="noopener">
            Validate this page in Google's Rich Results Test ↗
          </a>{' '}
          · or paste the JSON-LD into the{' '}
          <a href="https://validator.schema.org/" target="_blank" rel="noopener">
            Schema.org Validator ↗
          </a>
        </p>
      )}

      <EntityNav
        generations={generations}
        active={clampedActive}
        busy={busy}
        generatingType={generatingType}
        onSelect={setActiveEntity}
        onRemove={onRemove}
        facts={facts}
        onAddCompanion={onAddCompanion}
      />

      {activeResult && activeResult.fieldNotes.length > 0 && (
        <details style={{ marginTop: 20 }}>
          <summary style={{ cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 14, color: 'var(--ink-3)' }}>
            Field-by-field notes for {activeResult.type} ({activeResult.fieldNotes.length})
          </summary>
          <div className="card card-paper-2" style={{ marginTop: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--rule)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 0', fontWeight: 500, color: 'var(--ink-3)' }}>Field</th>
                  <th style={{ textAlign: 'left', padding: '8px 0', fontWeight: 500, color: 'var(--ink-3)' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 0', fontWeight: 500, color: 'var(--ink-3)' }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {activeResult.fieldNotes.map((n, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--rule)' }}>
                    <td style={{ padding: '10px 16px 10px 0', fontFamily: 'var(--font-mono)', fontSize: 12.5, verticalAlign: 'top' }}>
                      {n.field}
                    </td>
                    <td style={{ padding: '10px 16px 10px 0', verticalAlign: 'top' }}>
                      <StatusPill status={n.status} />
                    </td>
                    <td style={{ padding: '10px 0', color: 'var(--ink-2)', verticalAlign: 'top' }}>
                      {n.note || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
};

const EntityNav: React.FC<{
  generations: GenerationResult[];
  active: number;
  busy: boolean;
  generatingType: string | null;
  onSelect: (i: number) => void;
  onRemove: (i: number) => void;
  facts: PageFacts;
  onAddCompanion: (typeName: string, augment: boolean) => void;
}> = ({ generations, active, busy, generatingType, onSelect, onRemove, facts, onAddCompanion }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerType, setPickerType] = useState('');

  const usedTypes = new Set(generations.map((g) => g.type));
  const primaryType = generations[0]?.type || 'default';
  const suggestions = (COMPANION_SUGGESTIONS[primaryType] || COMPANION_SUGGESTIONS.default).filter(
    (t) => !usedTypes.has(t)
  );
  const remaining = ALL_TYPES.filter((t) => !usedTypes.has(t));

  const submitCompanion = (type: string) => {
    const augment = hasExistingOfType(facts.existingJsonLd, type);
    onAddCompanion(type, augment);
    setShowPicker(false);
    setPickerType('');
  };

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <span className="eyebrow" style={{ marginRight: 8 }}>Entities</span>
        {generations.map((g, i) => {
          const isActive = i === active;
          return (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px 6px 12px',
                borderRadius: 999,
                border: `1px solid ${isActive ? 'var(--ink)' : 'var(--rule)'}`,
                background: isActive ? 'var(--ink)' : 'var(--paper)',
                color: isActive ? 'var(--paper)' : 'var(--ink-2)',
                fontSize: 13,
                fontFamily: 'var(--font-mono)',
              }}
            >
              <button
                type="button"
                onClick={() => onSelect(i)}
                style={{ background: 'transparent', border: 0, padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {schemaTypeIcon(g.type, { size: 14 })}
                </span>
                {g.type}
                {g.augmented && <span title="Augmented from existing" style={{ marginLeft: 2, opacity: 0.7 }}>↺</span>}
              </button>
              {generations.length > 1 && (
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  aria-label={`Remove ${g.type}`}
                  style={{
                    background: 'transparent',
                    border: 0,
                    padding: '2px 4px',
                    margin: '-2px -2px -2px 2px',
                    color: 'inherit',
                    cursor: 'pointer',
                    opacity: 0.6,
                    fontSize: 14,
                    lineHeight: 1,
                  }}
                  title={`Remove ${g.type}`}
                >
                  ×
                </button>
              )}
            </span>
          );
        })}
        {!showPicker && remaining.length > 0 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setShowPicker(true)}
            disabled={busy}
            style={{ padding: '6px 12px', borderRadius: 999 }}
          >
            + Add companion
          </button>
        )}
      </div>

      {showPicker && (
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span className="eyebrow">Add a companion type</span>
            <button
              type="button"
              className="btn btn-sm"
              style={{ background: 'transparent', color: 'var(--ink-3)', padding: '4px 0' }}
              onClick={() => { setShowPicker(false); setPickerType(''); }}
            >
              Cancel
            </button>
          </div>
          {suggestions.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <span className="meta" style={{ display: 'block', marginBottom: 8 }}>
                Common pairings with <code>{primaryType}</code>
              </span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {suggestions.map((t) => {
                  const isLoading = generatingType === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => submitCompanion(t)}
                      disabled={busy}
                      style={{ borderRadius: 999, padding: '6px 14px 6px 10px', fontFamily: 'var(--font-mono)', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      {isLoading ? (
                        <span className="spinner" />
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--signal)' }}>
                          {schemaTypeIcon(t, { size: 14 })}
                        </span>
                      )}
                      {t}
                      {hasExistingOfType(facts.existingJsonLd, t) && (
                        <span className="pill pill-good" style={{ marginLeft: 4, fontSize: 9 }}>On page</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <span className="meta" style={{ display: 'block', marginBottom: 8 }}>
              Or pick any allowed type
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                className="input"
                value={pickerType}
                onChange={(e) => setPickerType(e.target.value)}
                style={{ flex: 1, fontFamily: 'var(--font-body)', fontSize: 14 }}
              >
                <option value="">— Choose a type —</option>
                {remaining.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => pickerType && submitCompanion(pickerType)}
                disabled={busy || !pickerType}
              >
                {generatingType === pickerType ? <><span className="spinner" /> Adding…</> : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ValidationPanel: React.FC<{ report: ValidationReport }> = ({ report }) => {
  const [expanded, setExpanded] = useState(!report.ok);

  if (report.issues.length === 0) {
    return (
      <div
        style={{
          marginBottom: 20,
          padding: '12px 16px',
          background: 'var(--score-high-wash)',
          border: '1px solid rgba(46,125,79,0.25)',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <span className="pill pill-good">Valid</span>
        <span style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>
          Schema validates against required fields, formats, and type-specific rules.
        </span>
      </div>
    );
  }

  const tone = report.errors > 0 ? 'critical' : 'warning';
  const bg = tone === 'critical' ? 'var(--score-low-wash)' : 'var(--score-mid-wash)';
  const border = tone === 'critical' ? 'rgba(179,58,58,0.3)' : 'rgba(181,132,42,0.3)';
  const fg = tone === 'critical' ? 'var(--score-low)' : 'var(--score-mid)';
  const summaryParts: string[] = [];
  if (report.errors) summaryParts.push(`${report.errors} error${report.errors === 1 ? '' : 's'}`);
  if (report.warnings) summaryParts.push(`${report.warnings} warning${report.warnings === 1 ? '' : 's'}`);
  if (report.infos) summaryParts.push(`${report.infos} info`);

  return (
    <div
      style={{
        marginBottom: 20,
        padding: '14px 18px',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span className={`pill pill-${tone}`}>Validation</span>
        <strong style={{ fontSize: 14, color: fg, fontWeight: 600 }}>
          {summaryParts.join(' · ')}
        </strong>
        <button
          type="button"
          className="btn btn-sm"
          style={{ background: 'transparent', color: 'var(--ink-3)', padding: '4px 0', marginLeft: 'auto' }}
          onClick={() => setExpanded((x) => !x)}
        >
          {expanded ? 'Hide details' : 'Show details'}
        </button>
      </div>
      {expanded && (
        <ul
          style={{
            margin: '12px 0 0',
            paddingLeft: 20,
            fontSize: 13.5,
            color: 'var(--ink-2)',
            lineHeight: 1.6,
          }}
        >
          {report.issues.map((iss, i) => (
            <ValidationLine key={i} issue={iss} />
          ))}
        </ul>
      )}
    </div>
  );
};

const ValidationLine: React.FC<{ issue: ValidationIssue }> = ({ issue }) => {
  const sevColor =
    issue.severity === 'error' ? 'var(--score-low)'
      : issue.severity === 'warning' ? 'var(--score-mid)'
        : 'var(--ink-3)';
  return (
    <li style={{ marginBottom: 6 }}>
      <strong style={{ color: sevColor, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.06em', marginRight: 8 }}>
        {issue.severity}
      </strong>
      <code style={{ marginRight: 6 }}>{issue.field}</code>
      <span>{issue.message}</span>
      {issue.reference && (
        <>
          {' '}
          <a href={issue.reference} target="_blank" rel="noopener" style={{ fontSize: 12 }}>
            ↗
          </a>
        </>
      )}
    </li>
  );
};

const StatusPill: React.FC<{ status: 'filled' | 'placeholder' | 'omitted' | 'preserved' }> = ({ status }) => {
  const labels: Record<typeof status, string> = {
    filled: 'Filled',
    placeholder: 'Placeholder',
    omitted: 'Omitted',
    preserved: 'Preserved',
  };
  const cls =
    status === 'filled' ? 'pill-good'
      : status === 'preserved' ? 'pill-info'
        : status === 'placeholder' ? 'pill-warning'
          : 'pill-info';
  return <span className={`pill ${cls}`}>{labels[status]}</span>;
};
